import type { JobPosting } from '@rumblefish/strapi/strapiTypes';
import type { AboutTheProjectProps } from './AboutTheProject/AboutTheProject.types';
import type { ContactProps } from './Contact/Contact.types';
import type { HeroProps } from './Hero/Hero.types';
import type { MetaData } from './JobPost.types';
import type { OurOfferProps } from './OurOffer/OurOffer.types';
import type { RequirementsProps } from './Requirements/Requirements.types';
import type { ResponsibilitiesProps } from './Responsibilities/Responsibilities.types';

export class JobPostData {
  constructor(private jobPost: JobPosting, private onApply: () => void) {}

  get metaData(): MetaData {
    const title = `Rumble Fish | Careers | ${this.jobPost.title}`;
    const meta = [
      {
        name: 'og:title',
        content: this.jobPost.title,
      },
    ];
    if (this.jobPost.facebookImage) {
      meta.push({
        name: 'og:image',
        content: this.jobPost.facebookImage.url,
      });
    }
    return { title, meta };
  }

  get heroProps(): HeroProps {
    const {
      jobTitle: title,
      image,
      locations,
      shortDescription,
      salaries,
    } = this.jobPost?.jobPage || {};
    return {
      title: title || '',
      heroImage: image?.url || '',
      locations:
        locations?.map((location) => location?.name?.toUpperCase() as string) ||
        [],
      shortDescription: shortDescription || '',
      salaries:
        salaries?.map((salary) => salary?.name?.toUpperCase() as string) || [],
      onApply: this.onApply,
    };
  }

  get requirementsProps(): RequirementsProps {
    const { technologiesList, greatIfYouKnowList, importantTraitsList } =
      this.jobPost?.jobPage || {};
    return {
      basicTechStack:
        technologiesList?.map((technology) => technology?.name as string) || [],
      greatIfYouKnow:
        greatIfYouKnowList?.map((technology) => technology?.name as string) ||
        [],
      importantTraits:
        importantTraitsList?.map((trait) => trait?.name as string) || [],
    };
  }

  get responsibilitiesProps(): ResponsibilitiesProps {
    const { whatYouWillDo } = this.jobPost?.jobPage || {};
    return {
      responsibilities:
        whatYouWillDo
          ?.map((responsibility) => responsibility?.name)
          .filter((name): name is string => !!name) || [],
    };
  }

  get ourOfferProps(): OurOfferProps {
    const { ourOfferAlsoIncludeList: benefits } = this.jobPost?.jobPage || {};
    return {
      benefits:
        benefits?.map((benefit) => ({
          name: benefit?.headline || '',
          icon: benefit?.icon?.url || '',
        })) || [],
    };
  }

  get aboutTheProjectProps(): AboutTheProjectProps {
    const { aboutTheProject } = this.jobPost?.jobPage || {};
    return { aboutTheProject: aboutTheProject ?? '' };
  }

  get contactProps(): ContactProps {
    const { jobTitle } = this.jobPost?.jobPage || {};
    return {
      jobTitle: jobTitle || '',
      color: 'orange',
      id: 'job-post-contact',
      title: 'Apply now for this position!',
    };
  }
}
